/**
 * Subscription tiers, and the Stripe price → tier map.
 *
 * Three tiers:
 *   free — tracking only.
 *   plus — adds the Strategy tab (split buy/sell, compare, multi-wallet).
 *   pro  — adds the full Tax tab (Australian CGT).
 *
 * ── Why the tier comes from the PRICE ────────────────────────────────────────
 *
 * `user_subscriptions.status` says whether someone is paying; it cannot say what
 * they are paying FOR. The Stripe webhook has written `price_id` on every
 * subscription event since the integration shipped, so the price is the one
 * piece of data that distinguishes Plus from Pro — and it is already populated
 * for existing rows, so no backfill is needed.
 *
 * ── Configuration ───────────────────────────────────────────────────────────
 *
 * Four build-time env vars, all optional:
 *
 *   REACT_APP_STRIPE_PLUS_PRICE_ID
 *   REACT_APP_STRIPE_PLUS_ANNUAL_PRICE_ID
 *   REACT_APP_STRIPE_PRO_PRICE_ID
 *   REACT_APP_STRIPE_PRO_ANNUAL_PRICE_ID
 *
 * Price IDs are not secrets — they identify a product, not a person, and are
 * useless without the secret key — so shipping them to the client is fine.
 *
 * EACH VAR ACCEPTS A COMMA-SEPARATED LIST, which is how test and live mode are
 * handled at once:
 *
 *   REACT_APP_STRIPE_PRO_PRICE_ID=price_1TzbnPtest...,price_1AbcLive...
 *
 * Test-mode and live-mode prices are different objects with different IDs, so a
 * map holding only one set breaks the moment the keys are swapped. Listing both
 * means the switch to live is additive: set the live IDs alongside the test ones
 * ahead of time, flip the secret key, and no deploy sits in a window where a
 * paying customer's price is unrecognised. (Per-environment Vercel vars work too
 * — a single ID per var is just a list of length one.)
 *
 * ── Failing OPEN, deliberately ──────────────────────────────────────────────
 *
 * An active subscription whose price is not in the map resolves to **pro**, not
 * free. The two failure modes are not symmetric: over-granting gives one person
 * more than they paid for, quietly and recoverably, while under-granting locks
 * a paying customer out of the thing they are paying for. A missing env var in a
 * production deploy would do the latter to everyone at once.
 *
 * The cost is that a genuinely misconfigured deploy gives all paying users
 * everything, which is why the fallback sets `assumed` and the provider logs it
 * once per unrecognised price.
 */

export type Tier = 'free' | 'plus' | 'pro';

/**
 * A tier that can actually be bought. `free` is the absence of a subscription,
 * not a product, so it can never be a checkout selection — expressing that in the
 * type means no call site has to decide what "check out the free tier" means.
 */
export type PaidTier = Exclude<Tier, 'free'>;

/** How often a subscription bills. */
export type BillingPeriod = 'monthly' | 'annual';

/**
 * What the client asks checkout for.
 *
 * Tier AND period, because a tier alone no longer identifies a price: there are
 * four of them. This is the shape that crosses both boundaries — the POST body to
 * /api/create-checkout-session, and the localStorage bridge from the landing page
 * through sign-up (see lib/pendingPlan).
 */
export interface CheckoutSelection {
  tier: PaidTier;
  period: BillingPeriod;
}

/** Runtime guard — values arriving from HTTP or localStorage are not typed. */
export function isPaidTier(value: unknown): value is PaidTier {
  return value === 'plus' || value === 'pro';
}

/** Runtime guard — see isPaidTier. */
export function isBillingPeriod(value: unknown): value is BillingPeriod {
  return value === 'monthly' || value === 'annual';
}

/**
 * Headline pricing, in whole AUD.
 *
 * One place, because it is quoted on the landing page, in the upgrade modal and
 * in Settings — and a price that disagrees with what Stripe actually charges is
 * the worst kind of copy bug. These must match the Stripe prices the
 * STRIPE_*_PRICE_ID vars point at; nothing enforces that, so change them together.
 */
export const TIER_PRICING: Record<PaidTier, { monthly: number; annual: number }> = {
  plus: { monthly: 10, annual: 96 },
  pro: { monthly: 15, annual: 144 },
};

/** What a tier costs per month on a given billing period (annual ÷ 12). */
export function monthlyPrice(tier: PaidTier, period: BillingPeriod): number {
  const p = TIER_PRICING[tier];
  return period === 'annual' ? p.annual / 12 : p.monthly;
}

/** Whole-percent saving from paying annually. Both tiers are 20%. */
export function annualSavingPercent(tier: PaidTier): number {
  const p = TIER_PRICING[tier];
  return Math.round((1 - p.annual / (p.monthly * 12)) * 100);
}

/** Display name for a tier. One place, so the three screens that name it agree. */
export const TIER_LABEL: Record<Tier, string> = { free: 'Free', plus: 'Plus', pro: 'Pro' };

/** Ordering, so "at least Plus" is expressible without enumerating tiers. */
export const TIER_RANK: Record<Tier, number> = { free: 0, plus: 1, pro: 2 };

/** Whether `tier` includes everything `minimum` does. */
export function tierAtLeast(tier: Tier, minimum: Tier): boolean {
  return TIER_RANK[tier] >= TIER_RANK[minimum];
}

/**
 * Stripe statuses that grant paid access.
 *
 * `past_due` IS included. A renewal whose card declines goes past_due while
 * Stripe retries (Smart Retries / dunning), and most of those recover. Locking a
 * paying customer out of what they bought the moment one charge fails punishes
 * them for something the retry usually fixes. Access ends when Stripe gives up:
 * the subscription then moves to `unpaid` or `canceled`, neither of which is here.
 *
 * `incomplete` and `incomplete_expired` are NOT paid: the first payment never
 * succeeded, so nothing was bought.
 *
 * Mirrored in api/create-checkout-session.js and api/stripe-webhook.js.
 */
export const PAID_STATUSES: ReadonlySet<string> = new Set(['active', 'trialing', 'past_due']);

export interface PriceTierMap {
  readonly plus: readonly string[];
  readonly pro: readonly string[];
}

/**
 * Split one env var into price IDs.
 *
 * Tolerant of the shapes a hand-edited env var actually takes: surrounding
 * whitespace, a trailing comma, an empty value. Case is preserved — Stripe IDs
 * are case-sensitive.
 */
export function parsePriceIds(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** The map built from the environment. Empty lists when nothing is configured. */
export const PRICE_TIER_MAP: PriceTierMap = {
  plus: [
    ...parsePriceIds(process.env.REACT_APP_STRIPE_PLUS_PRICE_ID),
    ...parsePriceIds(process.env.REACT_APP_STRIPE_PLUS_ANNUAL_PRICE_ID),
  ],
  pro: [
    ...parsePriceIds(process.env.REACT_APP_STRIPE_PRO_PRICE_ID),
    ...parsePriceIds(process.env.REACT_APP_STRIPE_PRO_ANNUAL_PRICE_ID),
  ],
};

export interface TierState {
  tier: Tier;
  /** Strategy tab: Plus and above. */
  canPlan: boolean;
  /** Tax tab: Pro only. */
  canTax: boolean;
  /**
   * True when the tier came from the fail-open fallback rather than the map —
   * i.e. the subscription is paid but its price is unrecognised. The signal that
   * the map has a gap; without it a misconfiguration is invisible because
   * everything appears to work.
   */
  assumed: boolean;
}

const FREE_STATE: TierState = { tier: 'free', canPlan: false, canTax: false, assumed: false };

/** Capabilities for a tier. One place, so no gate re-derives them. */
export function capabilitiesFor(tier: Tier): { canPlan: boolean; canTax: boolean } {
  return {
    canPlan: tierAtLeast(tier, 'plus'),
    canTax: tierAtLeast(tier, 'pro'),
  };
}

/**
 * Resolve a subscription row into a tier and its capabilities.
 *
 * Pure: the map is a parameter so tests can pin it without touching the
 * environment, and nothing here logs. The provider handles diagnostics.
 */
export function resolveTierState(
  status: string | null | undefined,
  priceId: string | null | undefined,
  map: PriceTierMap = PRICE_TIER_MAP
): TierState {
  // Not paying — no price lookup needed, and a stale price_id on a cancelled row
  // must not grant anything. (`customer.subscription.deleted` sets status but
  // leaves price_id in place, so that row really does exist.)
  if (!status || !PAID_STATUSES.has(status)) return FREE_STATE;

  const id = (priceId ?? '').trim();
  if (id) {
    // Plus first: a price in both lists is a configuration error, and granting
    // the lesser tier makes it visible rather than silently free.
    if (map.plus.includes(id)) return { tier: 'plus', ...capabilitiesFor('plus'), assumed: false };
    if (map.pro.includes(id)) return { tier: 'pro', ...capabilitiesFor('pro'), assumed: false };
  }

  // Paying, but the price is unrecognised or absent. See the header: over-grant.
  return { tier: 'pro', ...capabilitiesFor('pro'), assumed: true };
}

// ── Diagnostics ──────────────────────────────────────────────────────────────

const warnedPriceIds = new Set<string>();

/**
 * Log an unrecognised price once per ID per session.
 *
 * Deduped because the resolver runs on every subscription read; a bare
 * console.warn would bury the signal in repetition.
 */
export function warnUnmappedPrice(priceId: string | null | undefined): void {
  const id = (priceId ?? '').trim() || '(none)';
  if (warnedPriceIds.has(id)) return;
  warnedPriceIds.add(id);
  // eslint-disable-next-line no-console
  console.warn(
    `[tiers] Active subscription with unrecognised price ${id} — assuming Pro. ` +
      'Add it to REACT_APP_STRIPE_{PLUS,PRO}_{,ANNUAL_}PRICE_ID (comma-separated for test + live).'
  );
}

/** Reset the once-per-session warning state. Tests only. */
export function resetPriceWarnings(): void {
  warnedPriceIds.clear();
}
